import * as localForage from 'localforage';

import { createStore } from '@ngneat/elf';
import { entitiesPropsFactory, withActiveId, withActiveIds, withEntities } from '@ngneat/elf-entities';
import {
	createRequestsCacheOperator,
	createRequestsStatusOperator,
	initializeAsPending,
	withRequestsCache,
	withRequestsStatus,
} from '@ngneat/elf-requests';
import { GuestTypeSettings, UnassignedManifestGuest, VettingForm } from '@pages/manifests/interfaces/manifest.interface';
import { DefaultObject } from '@shared/interfaces/index';
import { persistState } from '@shared/third-party/persist-state/persist-state';

export interface EmergencyContact {
	id: number;
	firstName: string;
	lastName: string;
	phone: string;
}

export interface FirstName {
	required: boolean;
	provided: boolean;
}

export interface LastName {
	required: boolean;
	provided: boolean;
}

export interface Email {
	required: boolean;
	provided: boolean;
	unique: boolean;
}

export interface Phone {
	required: boolean;
	provided: boolean;
}

export interface AddressLine1 {
	required: boolean;
	provided: boolean;
}

export interface AddressCity {
	required: boolean;
	provided: boolean;
}

export interface AddressState {
	required: boolean;
	provided: boolean;
}

export interface AddressZip {
	required: boolean;
	provided: boolean;
}

export interface AddressCountry {
	required: boolean;
	provided: boolean;
}

export interface LicenseNumber {
	required: boolean;
	provided: boolean;
}

export interface LicensePhoto {
	required: boolean;
	provided: boolean;
}

export interface DateOfBirth {
	required: boolean;
	provided: boolean;
	minimumAgeRequirement: boolean;
	minimumAgeRequirementMet: boolean;
}

export interface EmergencyContacts {
	required: boolean;
}

export interface VettingQuestions {
	required: boolean;
	provided: boolean;
	qualified: boolean;
}

export interface Documents {
	checkinCompleted: boolean;
	preCheckinCompleted: boolean;
	acknowledgement: Document;
	navigationRules: Document;
	rentalAgreement: Document;
	waiver: Document;
}

export interface Document {
	required: boolean;
	accepted: boolean;
	preCheckinAcceptanceRequired: boolean;
	signed: boolean;
	preCheckinSigningRequired: boolean;
}

export interface CheckinDetails {
	firstName: FirstName;
	lastName: LastName;
	email: Email;
	phone: Phone;
	addressLine1: AddressLine1;
	addressCity: AddressCity;
	addressState: AddressState;
	addressZip: AddressZip;
	addressCountry: AddressCountry;
	licenseNumber: LicenseNumber;
	licensePhoto: LicensePhoto;
	dateOfBirth: DateOfBirth;
	emergencyContacts: EmergencyContacts;
	vettingQuestions: VettingQuestions;
	documents: Documents;
}

export interface ApprovalSignatures {
	manifestGuestId: number;
	onBehalfOfManifestGuestId: number;
	url: string;
}

export interface Guest {
	acceptedAcknowledgement?: boolean;
	acceptedNavigationRules?: boolean;
	acceptedRentalAgreement?: boolean;
	acceptedWaiver?: boolean;
	accompaniedMinor: boolean;
	addressCity?: string;
	addressCountry?: string;
	addressLine1?: string;
	addressLine2?: string;
	addressStateIso2?: string;
	addressZip?: string;
	approvalInitals?: any[]
	approvalSignatures?: ApprovalSignatures[];
	checkinCompleted?: boolean;
	companyId?: number;
	customerId?: number;
	dateOfBirth?: string;
	dlNumber?: string;
	dlState?: string;
	email?: string;
	firstName: string;
	guardianManifestGuestId?: number;
	guardianManifestGuestIds?: number[];
	id: number;
	isComplete?: boolean;
	isDocumentsCompleted?: boolean;
	isRegistered?: boolean;
	isMinor?: boolean;
	minor: boolean;
	isPrimaryOperator?: boolean;
	isVettingQuestionsCompleted?: boolean;
	lastName: string;
	licensePhotoProvided?: boolean;
	manifestGuestTypeId: number;
	manifestProductId?: number;
	phone?: string;
	licenseNumber?: String;
	signature?: string;
	signatureProvided?: boolean;
	vettingFormFieldResponses?: DefaultObject;
	emergencyContacts?: EmergencyContact[];
	checkinDetails?: CheckinDetails;
	failedRegistration?: boolean;
	isLicenseRequired?: boolean;
	isLicenseProvided?: boolean;
	isDocumentsRequired: boolean;
	isDocumentsProvided?: boolean;
	guestState?: { [key: string]: any };
	guestCompleted?: boolean;
	guardian?: boolean;
	minorManifestGuestIds?: number[];
    reservationHost?: boolean;
    insurancePreSelector?: boolean;
}

export interface Product {
	finalized?: boolean;
	guestTypeConfig: GuestTypeSettings[];
	id: number;
	insuranceDeclined: boolean;
	insurancePolicyId?: string;
	insurancePurchased: boolean;
	insuranceQuoteId: string;
	insuranceRequired: boolean;
	inventoryTypeId: string;
	locked?: boolean;
	manifestGuests?: Array<Guest['id']>;
	maxCapacity?: number;
	productId: number;
	productName?: string;
	productType: string;
	productTypeId: number;
	vettingForm?: VettingForm;
	refresh?: number;
	isReady?: boolean;
}

export interface Manifest {
	activeProductId?: number;
	cardGuid?: string;
	checkInEnabled: boolean;
	companyId: number;
	customerGuid?: string;
	customerId?: number;
	finalized: boolean;
	guests: Array<Guest['id']>;
	unassignedManifestGuests: Array<UnassignedManifestGuest['id']>;
	id: number;
	locked: boolean;
	products: Array<Product['id']>;
	reservationId: number;
	meta?: { [key: string]: any };
	insurancePreselectionCoverageTypeId: number;
}

export const { productsEntitiesRef, withProductsEntities } = entitiesPropsFactory('products');

export const { guestsEntitiesRef, withGuestsEntities } = entitiesPropsFactory('guests');

export const { unassignedGuestsEntitiesRef, withUnassignedGuestsEntities } = entitiesPropsFactory('unassignedGuests');

export const store = createStore(
	{ name: 'manifests' },
	withEntities<Manifest, 'id'>({ idKey: 'id' }),
	withRequestsStatus<`manifests` | `manifest-${number}`>(initializeAsPending('manifests')),
	withRequestsCache<`manifests` | `manifest-${number}`>(),
	withProductsEntities<Product>(),
	withGuestsEntities<Guest>(),
	withUnassignedGuestsEntities<UnassignedManifestGuest>(),
	withActiveId(),
	withActiveIds()
);
export const trackCustomersRequestsStatus = createRequestsStatusOperator(store);
export const persist = persistState(store, {
	key: 'manifests',
	storage: localForage,
});
export const skipWhileCustomersCached = createRequestsCacheOperator(store);
